import React, { Dispatch, useReducer } from "react";
import jwtDecode from "jwt-decode";
import Cookies from "js-cookie";
import { addDays } from "date-fns";

const AuthContext = React.createContext<[AuthState,Dispatch<Action>] | undefined>(undefined);
AuthContext.displayName = "AuthContext";

export enum UserType {
  Admin = "Admin",
  Customer ="Customer"
}

export interface TokenData {
  name: string;
  sub: string;
  email: string;
}

type Action =
  | { type: "login"; token: string; staySignedIn: boolean; refresh: string }
  | { type: "logout" };

interface AuthState {
  token: string | null;
  isLoggedIn: boolean;
  user: Partial<TokenData>;
}

const reducer = (state: AuthState, action: Action):AuthState => {
  switch (action.type) {
    case "login":
      if (action.staySignedIn) {
        saveTokens(action.token, action.refresh, addDays(new Date(),90));
      } else {
        saveTokens(action.token, action.refresh);
      }
      return {
        token: action.token,
        isLoggedIn: true,
        user: jwtDecode(action.token),
      };

    case "logout":
      logout();
      return { token: null, isLoggedIn: false, user: {} };

    default:
      return state;
  }
};

export function saveTokens(token:string,refreshToken:string,expirationDate?:Date) {
  Cookies.set('_token' , token,{expires:expirationDate});
  Cookies.set('_refresh' , refreshToken,{expires:expirationDate});
  expirationDate && Cookies.set('_remember', expirationDate.toString());
}


export function getTokens() {
  return {
    token : Cookies.get('_token'),
    refreshToken : Cookies.get('_refresh'),
    expirationDate : Cookies.get('_remember')
  }
}

export function removeTokens() {
  Cookies.remove('_token');
  Cookies.remove('_refresh');
  Cookies.remove('_remember');
}

export async function logout() {
    removeTokens();
    window.location.assign("/login");
}

export function AuthProvider({ ...props }) {
  const { token } = getTokens();
  const user  = token ? jwtDecode<TokenData>(token) : {} as any;

  const [state, dispatch] = useReducer(reducer, {
    token: token || null,
    isLoggedIn: !!token,
    user
  });

  return <AuthContext.Provider value={[state, dispatch]} {...props} />;
}

export function useAuthContext() {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext hook must be wrapped in AuthProvider");
  }

  return context;
}